import {
	CANONICAL_ORIGINS, CanonicalOrigins, LocalePathMap, SITE_LOCALES, SiteLocale, canonicalLocationFor,
	hreflangAlternatesFor, symmetricPaths,
} from "./siteMeta.ts";
import {ADVERTISING_TERMS_PATH} from "../sponsor/sponsorRoutes.ts";

/**
 * Sitemap generation (ui#97, per-domain in ui#103, locale-asymmetric in ui#1003). The list of
 * indexable public routes lives here, next to the other SEO metadata, and {@link ../../vite.config.ts}
 * renders ONE SITEMAP FILE PER SHIPPED LOCALE TREE from it at build time — `build/sitemap.xml`
 * (transferify.eu: the English pages, plus the German ones since kitecorp/transferify-ui#3518, see
 * {@link sitemapLocalesFor}), `build/ro/sitemap.xml` (transferify.ro) and `build/de/sitemap.xml` (the
 * German pages, which transferify.de served until it began redirecting, kitecorp/devops#814) — so no sitemap can silently drift
 * from the routes we actually ship. The emitter walks `SITE_LOCALES` rather than naming files, so a
 * market's sitemap exists the day its tree does; this note still said TWO once the German market had
 * shipped (ui#1498), which is how a doc that names the files stops describing the build.
 * Each route carries a locale→path map: a route is listed on a locale's
 * origin only where that locale's tree ships it, so a Romanian-only page never appears in the English
 * sitemap as a URL that 404s. Only *indexable* public pages belong here: the private auth-gated
 * routes and the `noindex` landing shells (`/download`, `/reminder-uploads`) are deliberately
 * excluded.
 */

export interface DatedSitemapRoute {
	/**
	 * The route's per-locale paths (ui#1003): which language trees ship it, and the path it lives at in
	 * each. Most routes are {@link symmetricPaths}; a bilingual content page may map DIFFERENT slugs
	 * per locale, and a single-locale route maps only its own locale — `renderSitemapXml` then lists it
	 * on that locale's origin only.
	 */
	paths: LocalePathMap;
	/** `<changefreq>` hint. */
	changefreq: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
	/** `<priority>` (0.0–1.0) as a string, e.g. "0.8". */
	priority: string;
	/**
	 * `<lastmod>` for THIS url, as ISO `YYYY-MM-DD`, PER LOCALE (#1542, per locale in
	 * kitecorp/transferify-ui#2734).
	 *
	 * Per route rather than one date for the document, because that is the only form in which the
	 * field carries information: a sitemap that stamps the build date on all 41 urls tells a crawler
	 * that every page changed on every deploy, including deploys that changed no page at all.
	 *
	 * Per LOCALE for the same reason one step down. A page's revision is a fact about the TREE that
	 * publishes it, not about the route: transferify.de first published on 2026-09-09, so its copy of
	 * every page shipped before that date came into being then, whatever date the English page carries.
	 * One shared value told Google that every German page — the priority-1.0 home page included — had
	 * last changed two weeks before the site existed. A locale the route ships in must have an entry
	 * here; {@link renderSitemapXml} refuses a route that ships in a locale it has no date for.
	 */
	lastmod: LocaleDateMap;
}

/** A `<lastmod>` per locale, as ISO `YYYY-MM-DD` — the shape {@link DatedSitemapRoute.lastmod} takes. */
export type LocaleDateMap = Readonly<Partial<Record<SiteLocale, string>>>;

/**
 * A route in the shipping table: everything {@link DatedSitemapRoute} carries, except that it declares
 * HOW its `<lastmod>` is determined rather than the dates themselves.
 *
 * The two are separate types because no date is knowable from this module. The legal pages' dates live
 * in their own copy, which is `.tsx` and therefore out of reach of anything that must stay React-free,
 * and every other page's lives in git history (this module is imported by the Vite config, by the
 * language switcher and by node-environment tests alike), so {@link datedSitemapRoutes} resolves them
 * from what the build reads out of the copy and out of git.
 */
export interface SitemapRoute extends Omit<DatedSitemapRoute, "lastmod"> {
	revision: RouteRevision;
}

/** How one route's `<lastmod>` is determined for each locale that ships it. */
export type RouteRevision =
	/**
	 * The date of the newest commit to any file that renders this page, as `./pageSources.ts` lists them
	 * for its path (kitecorp/transferify-ui#3611), floored per locale at the date that tree first
	 * published, because no tree revised a page before it had one. The list is not here because this
	 * module reaches the browser through the language switcher, and the list is build-only.
	 */
	| {readonly fromPageSources: true}
	/**
	 * Read from the date this locale's own copy of that legal body states. The "Last updated" /
	 * "Ultima actualizare" / "Stand" line IS the revision claim the page makes to its reader, so a
	 * second date kept beside it can only ever drift — and had, by a month, on three live pages.
	 */
	| {readonly statedIn: LegalDocument};

/**
 * A page's last-change date in one locale's tree, as ISO `YYYY-MM-DD`, by the page's path in that tree:
 * what the build reads from git through `./pageSources.ts` `pageRevisions`.
 */
export type PageRevisions = (path: string, locale: SiteLocale) => string;

/** The legal bodies whose own copy states the date they were last revised. */
export type LegalDocument =
	"terms" | "privacy" | "cookie" | "impressum" | "dataDeletion" | "advertisingTerms";

/**
 * Each dated legal body's revision date per locale, read from that locale's own copy by
 * `scripts/legalRevisions.mjs` — the build's input to {@link datedSitemapRoutes}.
 */
export type LegalRevisions = Readonly<Record<LegalDocument, LocaleDateMap>>;

/**
 * The date each market's language tree first published pages.
 *
 * The floor under every {@link RouteRevision} `fromPageSources` date, and the one fact this file has to state
 * rather than derive: a market's launch is not written anywhere else in the tree. Each value is the
 * commit that added that tree's catalog (`git log --diff-filter=A -- src/i18n/<locale>.ts`), which for
 * German is 2026-09-09 — the same day Search Console first saw transferify.de serve a page. English is
 * the original tree, so its floor is the repository's first commit and never binds; it is listed
 * anyway, because a total table cannot be read as "this locale was forgotten".
 *
 * One date per MARKET, not per route per locale. A per-locale date on every route would be 48 hand-kept
 * values to keep true, which is the maintenance that produced the defect this replaces.
 */
export const LOCALE_TREE_PUBLISHED: Readonly<Record<SiteLocale, string>> = {
	en: "2019-10-29",
	ro: "2026-07-10",
	de: "2026-09-09",
	// French began on the day its catalogue was added (kitecorp/transferify-ui#3937), as German did, and it is built
	// behind `REACT_APP_FRENCH`, so the floor sits on or before the day the tree goes public.
	fr: "2026-10-31",
	// Italian began on the day its catalogue was added (kitecorp/transferify-ui#3984), behind `REACT_APP_ITALIAN`.
	it: "2026-11-01",
};

/** The public, indexable routes, in descending priority. */
export const SITEMAP_ROUTES: SitemapRoute[] = [
	{paths: symmetricPaths("/"), changefreq: "weekly", priority: "1.0", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/features"), changefreq: "monthly", priority: "0.8", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/pricing"), changefreq: "monthly", priority: "0.8", revision: {fromPageSources: true}},
	// Ad-landing / explainer pages (ui#133). Locale-less paths: every tree serves them at the same path,
	// so each locale's sitemap lists this route at that path where the locale is canonical: the brand
	// origin, or `/de` on the main domain for German (kitecorp/transferify-ui#3518). It emits no `/ro`
	// mirror <loc> — since ui#2471 that prefixed URL is the declared DUPLICATE of the brand one, and this
	// comment said otherwise until kitecorp/transferify-ui#2734.
	{paths: symmetricPaths("/p2p"), changefreq: "monthly", priority: "0.8", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/client-server"), changefreq: "monthly", priority: "0.8", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/zero-knowledge"), changefreq: "monthly", priority: "0.7", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/about"), changefreq: "monthly", priority: "0.7", revision: {fromPageSources: true}},
	// The advertising PRESENTATION page — the buyer-facing entry point. The booking flow behind it
	// (`/advertise`) is deliberately not listed: one indexable door to the offer, and it is this one.
	{paths: symmetricPaths("/advertising"), changefreq: "monthly", priority: "0.6", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/contact"), changefreq: "monthly", priority: "0.6", revision: {fromPageSources: true}},
	{paths: symmetricPaths("/login"), changefreq: "monthly", priority: "0.5", revision: {fromPageSources: true}},
	// The § 312k BGB cancellation page (kitecorp/transferify-ui#2327). INDEXED on purpose, unlike the
	// booking flow above: Abs. 2 S. 4 requires it to be easily accessible, and search is how a consumer
	// who cannot find the menu finds the control.
	{paths: symmetricPaths("/cancel-contract"), changefreq: "yearly", priority: "0.4", revision: {fromPageSources: true}},
	// Every route below is a legal document that DATES ITSELF in its own copy, in each reader's own
	// language, so that line is what each one's `<lastmod>` is read from. Nothing here restates a date:
	// editing a legal body re-dates its sitemap entry, and `sitemap.test.ts` fails a document whose
	// entry and copy disagree.
	{paths: symmetricPaths("/terms"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "terms"}},
	// The Advertising Terms (kitecorp/transferify-ui#2602), beside the Terms because they are the
	// booking contract's own terms and a reader looking for one looks next to the other.
	{paths: symmetricPaths(ADVERTISING_TERMS_PATH), changefreq: "yearly", priority: "0.3", revision: {statedIn: "advertisingTerms"}},
	{paths: symmetricPaths("/privacy"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "privacy"}},
	{paths: symmetricPaths("/cookies"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "cookie"}},
	{paths: symmetricPaths("/data-deletion"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "dataDeletion"}},
	// Provider identification (kitecorp/transferify-ui#2291). Symmetric because all three trees serve
	// it at the same path and it identifies the same company on every brand, so each locale's copy is
	// a genuine alternate of the others.
	{paths: symmetricPaths("/impressum"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "impressum"}},
];

/**
 * The `<lastmod>` one route claims in one locale.
 *
 * @throws when a legal body states no date for this locale. A market whose policies are undated must
 *   not reach a sitemap at all: the alternative is publishing some other language's date, which is the
 *   class of claim this whole mechanism exists to stop making.
 */
export function lastmodFor(
	revision: RouteRevision,
	path: string,
	locale: SiteLocale,
	legal: LegalRevisions,
	pages: PageRevisions,
): string {
	if ("statedIn" in revision) {
		const stated = legal[revision.statedIn][locale];
		if (stated === undefined) {
			throw new Error(`The ${locale} ${revision.statedIn} copy states no revision date, `
				+ `so its sitemap entry has no <lastmod>`);
		}
		return stated;
	}
	const revised = pages(path, locale);
	// ISO dates compare chronologically as strings, which is why `<lastmod>` is ISO.
	const published = LOCALE_TREE_PUBLISHED[locale];
	return revised >= published ? revised : published;
}

/**
 * The shipping routes with every locale's `<lastmod>` resolved — what {@link renderSitemapXml} renders.
 *
 * @param legal each dated legal body's revision date per locale, read from that locale's own copy
 *   (`scripts/legalRevisions.mjs`).
 * @param pages each page's last-change date per locale, read from git (`./pageSources.ts`).
 * @param routes the table to date; the shipping {@link SITEMAP_ROUTES} by default, a caller-supplied
 *   table in tests.
 */
export function datedSitemapRoutes(
	legal: LegalRevisions,
	pages: PageRevisions,
	routes: readonly SitemapRoute[] = SITEMAP_ROUTES,
): DatedSitemapRoute[] {
	return routes.map(({revision, ...route}) => ({
		...route,
		lastmod: Object.fromEntries(SITE_LOCALES.flatMap((locale) => {
			const path = route.paths[locale];
			return path === undefined ? [] : [[locale, lastmodFor(revision, path, locale, legal, pages)]];
		})),
	}));
}

/** Absolute `<loc>` for a route path on the given origin (no trailing slash except the root). */
export function locFor(origin: string, path: string): string {
	return `${origin}${path === "/" ? "/" : path}`;
}

/**
 * One locale's routes as a sitemap file lists them: every `<loc>` is that locale's canonical location
 * for the route, and only the routes whose `paths` map the locale are listed.
 */
export interface SitemapSection {
	locale: SiteLocale;
	/**
	 * The DATED route table: {@link datedSitemapRoutes} over the shipping {@link SITEMAP_ROUTES} plus that
	 * locale's corpus, and a caller-supplied table in tests. Required rather than defaulted, because a
	 * route's dates are not knowable from this module alone — the legal pages take theirs from their own
	 * copy — and a default would have to invent them.
	 */
	routes: readonly DatedSitemapRoute[];
}

/**
 * Which locales' pages the sitemap file of one locale TREE lists (kitecorp/transferify-ui#3518).
 *
 * Each tree's file is served at the root of that tree's host (`build/sitemap.xml` on transferify.eu,
 * `build/ro/sitemap.xml` on transferify.ro), and a sitemap may only list URLs of the host it is served
 * on. So the main tree's file lists the English pages AND every market whose pages are canonical under
 * the main host's prefix — German, since transferify.de redirects to transferify.eu/de: that sitemap is
 * the one `transferify.eu/robots.txt` names and the one `transferify.de/sitemap.xml` redirects to
 * (kitecorp/devops#814), while the edge refuses `transferify.eu/de/sitemap.xml`. Every other tree's file
 * lists its own locale, which for German is a file no host serves since that redirect.
 *
 * @param tree the locale tree whose file is being rendered
 * @param origins where each locale is canonical, as `canonicalLocationFor` takes it
 */
export function sitemapLocalesFor(tree: SiteLocale, origins: CanonicalOrigins = CANONICAL_ORIGINS): SiteLocale[] {
	if (tree !== "en") {
		return [tree];
	}
	return SITE_LOCALES.filter((locale) => locale === "en" || origins[locale] === undefined);
}

/**
 * Render the sitemap XML for one locale's pages: {@link renderSitemapFile} with that locale alone.
 *
 * @param locale The language whose pages this sitemap lists — every `<loc>` is that locale's CANONICAL
 *   location for the route (`canonicalLocationFor`), and only routes whose `paths` map this locale are
 *   listed, so the .ro sitemap lists Romanian URLs and the German one German URLs under transferify.eu/de,
 *   each restricted to the routes that tree actually ships.
 * @param routes as {@link SitemapSection.routes}
 */
export function renderSitemapXml(
	locale: SiteLocale,
	routes: readonly DatedSitemapRoute[],
): string {
	return renderSitemapFile([{locale, routes}]);
}

/**
 * Render one sitemap FILE listing the pages of each section's locale, in section order — the main
 * tree's file lists the English pages, then the German ones ({@link sitemapLocalesFor}).
 *
 * Alternates are NOT a parameter: each `<url>` derives its own `<xhtml:link rel="alternate" hreflang=…>`
 * set from the route's locale→path map via {@link hreflangAlternatesFor} (so a paired route cross-links
 * its counterpart's — possibly localized — slug and a single-locale route carries none), and the `xhtml`
 * namespace is declared when any listed `<url>` carries at least one alternate.
 */
export function renderSitemapFile(sections: readonly SitemapSection[]): string {
	// Routes unavailable in a section's locale drop out entirely (flatMap keeps the order and needs no
	// intermediate type predicate); alternates are derived per route from its own map.
	//
	// Each `<loc>` is the route's CANONICAL location (ui#2471), not "this locale's origin plus this
	// locale's path": a sitemap's job is to list the URL that should be indexed, so a locale served under
	// a prefix on the main domain is listed there and a locale with a brand domain is listed on it. That is
	// also why the main domain's sitemap carries no `/ro/` url — those pages canonicalise to transferify.ro
	// and are listed in ITS sitemap — and does carry the `/de/` ones.
	const entries = sections.flatMap(({locale, routes}) => routes.flatMap((route) => {
		const path = route.paths[locale];
		return path === undefined
			? []
			: [{route, locale, path, location: canonicalLocationFor(locale, path), alternates: hreflangAlternatesFor(route.paths)}];
	}));
	const xhtmlNs = entries.some(({alternates}) => alternates.length > 0)
		? ` xmlns:xhtml="http://www.w3.org/1999/xhtml"`
		: "";
	const urls = entries.map(({route, locale, path, location, alternates}) => {
		const altLinks = alternates
			.map(
				(alt) =>
					`		<xhtml:link rel="alternate" hreflang="${alt.hreflang}" href="${locFor(alt.origin, alt.path)}"/>\n`,
			)
			.join("");
		// A route this locale ships must carry this locale's date. Refused rather than emitted empty or
		// filled from another locale: `<lastmod>` is the only field here a crawler acts on, and a wrong
		// one is worse than the element being absent. Named by the path the route table gives it in that
		// tree, which is what an author searching the table finds.
		const lastmod = route.lastmod[locale];
		if (lastmod === undefined) {
			throw new Error(`The sitemap route ${path} ships in ${locale} but carries no ${locale} <lastmod>`);
		}
		return (
			`	<url>\n` +
			`		<loc>${locFor(location.origin, location.path)}</loc>\n` +
			`		<lastmod>${lastmod}</lastmod>\n` +
			`		<changefreq>${route.changefreq}</changefreq>\n` +
			`		<priority>${route.priority}</priority>\n` +
			`${altLinks}` +
			`	</url>`
		);
	}).join("\n");
	return `<?xml version="1.0" encoding="UTF-8"?>\n` +
		`<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"${xhtmlNs}>\n` +
		`${urls}\n` +
		`</urlset>\n`;
}
